import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { SubscriptionResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/subscription/subscription.response';
import { CheckoutProviderService } from './checkout-provider.service';
import { CheckoutPlan } from './checkout.service';
import { PricingService } from './pricing.service';

/** Clé locale servant à détecter le passage premium → gratuit. */
const LAST_PREMIUM_KEY = 'vw-last-premium';

/** Durée maximale d'attente de l'activation après un paiement. */
const ACTIVATION_TIMEOUT_MS = 30_000;
const ACTIVATION_POLL_MS = 3_000;

/** Durée de l'essai maison, sans carte (`POST /subscription/trial`). */
export const TRIAL_DAYS = 30;

/** Un essai réellement offert ici et maintenant. */
export interface TrialOffer {
  days: number;
  /**
   * Essai du MAGASIN : il démarre par l'achat de la formule, et le magasin
   * prélève à son terme. Sinon, l'essai maison, sans carte.
   */
  store: boolean;
}

/**
 * État premium de l'utilisateur, côté app.
 *
 * Deux sources, à ne pas confondre :
 * - le CLAIM du jeton (`authApi.isPremium()`) : instantané, gratuit, mais il
 *   peut retarder d'une heure après un achat ou une résiliation. Bon pour
 *   choisir un libellé ou masquer un encart.
 * - l'API (`subscriptionApi.me()`) : la vérité, avec l'échéance et le canal.
 *
 * Aucune décision de sécurité ne se prend ici : le serveur refuse de lui-même
 * ce qui doit l'être. Ce service sert l'AFFICHAGE.
 *
 * L'interrupteur `premiumEnabled` vient lui aussi de l'API : le back est seul à
 * savoir si le premium est ouvert. Les écrans lisent `hasPremiumAccess` pour
 * montrer une fonction ou son cadenas, et `premiumEnabled` pour montrer ou non
 * une offre (paywall, encart, carte d'abonnement).
 */
@Injectable({ providedIn: 'root' })
export class PremiumService {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme partout ailleurs dans l'app (AuthApi n'est pas
  // fourni au niveau racine).
  private readonly authApi = new AuthApi();
  private readonly subscriptionApi = new SubscriptionApi();
  private readonly checkout = inject(CheckoutProviderService);
  /** L'offre d'essai du magasin arrive avec ses prix. */
  private readonly pricing = inject(PricingService);

  readonly isPremium = signal<boolean>(false);
  /**
   * Le premium est-il ouvert (`PREMIUM_ENABLED` du back) ?
   *
   * FERMÉ tant que le serveur n'a pas répondu le contraire : un appel en échec
   * ou un back qui ne connaît pas encore ce champ ne doivent jamais montrer une
   * offre qu'on ne peut pas acheter.
   */
  readonly premiumEnabled = signal<boolean>(false);
  /**
   * Accès aux fonctions premium : tout le monde quand le premium est fermé, les
   * abonnés sinon. `isPremium` reste l'abonnement RÉEL (carte « Membre »).
   */
  readonly hasPremiumAccess = computed(
    () => !this.premiumEnabled() || this.isPremium(),
  );
  readonly subscription = signal<SubscriptionResponse | null>(null);
  /** Une transition premium → gratuit vient d'être constatée. */
  readonly justDowngraded = signal<boolean>(false);
  /**
   * Une transition gratuit → premium vient d'être constatée.
   *
   * Porté par le service et non par l'écran d'achat : le paiement se conclut
   * dans le navigateur, et l'essai peut être lancé depuis plusieurs endroits.
   * La célébration doit se déclencher d'où que vienne l'activation.
   */
  readonly justUpgraded = signal<boolean>(false);

  /**
   * L'état premium est-il connu ? Vrai dès la construction : le jeton répond.
   *
   * Conservé pour les écrans qui préfèrent ne rien afficher plutôt qu'une
   * offre payante prématurée — mais il n'y a plus de fenêtre d'incertitude.
   */
  readonly ready = signal<boolean>(false);

  /**
   * Compte (`sub`) pour lequel le serveur a déjà répondu. Tant qu'il ne
   * correspond pas au compte connecté, l'état affiché n'est qu'une supposition
   * et `ensureServerState()` relance la lecture.
   */
  private serverStateSub: string | null = null;

  constructor() {
    // Le JETON répond tout de suite : le groupe `PREMIUM_USER` y figure, et
    // `decodeToken()` est synchrone — c'est ainsi que le reste de l'app lit
    // l'authentification (cf. `isGuest` du chatbot et du paywall).
    // Aucune raison de faire attendre l'interface pour ça.
    this.isPremium.set(this.authApi.isPremium());
    this.ready.set(true);

    // L'API corrige ensuite, en arrière-plan. Elle est indispensable au
    // lendemain d'un achat : le jeton en main ne porte pas encore le groupe et
    // ne le portera qu'à son renouvellement, jusqu'à une heure plus tard.
    void this.refresh();
  }

  /**
   * Relit le jeton puis l'état serveur.
   *
   * Appelé une fois le jeton hydraté depuis le stockage natif : au démarrage à
   * froid d'un WebView, il n'était pas encore en place à la construction.
   */
  async init(): Promise<void> {
    this.isPremium.set(this.authApi.isPremium());
    await this.refresh();
  }

  /**
   * Relit l'état auprès du serveur (sans toucher au jeton).
   *
   * @returns true si le serveur a répondu — donc si l'état est FIABLE. Un
   * échec laisse la valeur précédente : ne pas connaître l'abonnement ne doit
   * jamais empêcher d'utiliser l'app. Avant toute réponse, le premium est donc
   * fermé.
   *
   * Sans session (visiteur des pages publiques, ou jeton pas encore hydraté
   * depuis le stockage natif), il n'y a pas d'abonnement à lire : l'appel
   * revenait en 401. `init()` relit une fois le jeton en place.
   */
  async refresh(): Promise<boolean> {
    // Un jeton expiré donne un 401 certain : au démarrage, le jeton relu est
    // souvent celui de la veille et le garde de navigation ne l'a pas encore
    // renouvelé. Attendre `ensureServerState()`, appelé après la navigation.
    if (!this.hasLiveToken()) return false;
    try {
      const state = await this.subscriptionApi.me();
      this.premiumEnabled.set(state.premiumEnabled === true);
      this.subscription.set(state);
      this.applyPremiumState(state.isPremium);
      this.serverStateSub = this.currentSub();
      // En magasin, l'offre d'essai arrive avec les prix : la charger ici rend
      // la promesse juste partout (carte du profil, semaine lunaire), pas
      // seulement une fois le paywall ouvert.
      if (state.premiumEnabled && this.checkout.isStore) void this.pricing.load();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Lit l'état serveur s'il n'est pas encore connu pour le compte connecté.
   *
   * Appelé à chaque fin de navigation : le garde a alors renouvelé le jeton.
   * C'est le filet du démarrage, où la lecture du constructeur part avec un
   * jeton expiré et où `init()` attend une réponse native qui peut ne jamais
   * venir. Sans lui, l'offre et la carte d'abonnement restaient cachées toute
   * la session.
   */
  async ensureServerState(): Promise<boolean> {
    const sub = this.currentSub();
    if (!sub) return false;
    if (sub === this.serverStateSub) return true;
    this.isPremium.set(this.authApi.isPremium());
    return this.refresh();
  }

  /**
   * Force le renouvellement du jeton puis relit l'état.
   *
   * À appeler au RETOUR d'un achat : l'abonnement existe côté serveur, mais le
   * jeton en main ne porte pas encore le groupe premium. Sans cela,
   * l'utilisateur aurait payé et ne verrait rien changer pendant une heure.
   */
  async refreshEntitlement(): Promise<void> {
    try {
      await this.authApi.forceRefreshToken();
    } catch {
      // Le rafraîchissement peut échouer (réseau) : l'API reste la vérité.
    }
    await this.refresh();
  }

  /**
   * Attend l'activation après un paiement. Le webhook Stripe met quelques
   * secondes à nous parvenir : on interroge jusqu'à ce que le premium arrive,
   * sans jamais bloquer indéfiniment.
   *
   * @returns true si le premium est actif dans le délai imparti.
   */
  async awaitActivation(): Promise<boolean> {
    const deadline = Date.now() + ACTIVATION_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await this.refreshEntitlement();
      if (this.isPremium()) return true;
      await new Promise((resolve) => setTimeout(resolve, ACTIVATION_POLL_MS));
    }
    return this.isPremium();
  }

  /**
   * L'essai offert ici et maintenant, ou null. SEULE source de toute promesse
   * d'essai (paywall, carte du profil, encart de la semaine lunaire) : promettre
   * un essai qu'on ne peut pas lancer fait perdre la confiance au moment de payer.
   *
   * - Navigateur : l'essai maison de 30 jours, sans carte, une fois par compte.
   * - App des magasins : l'essai du MAGASIN, tel que la coque le lit (1.5.1+) ;
   *   débloquer l'app par notre propre mécanisme y est interdit (règle 3.1.1
   *   d'Apple). Pour une formule donnée, ou la plus longue des deux.
   *
   * Aucun essai :
   * - tant que le serveur n'a pas répondu, ou si le premium est fermé ;
   * - à un abonné, ou à qui a déjà consommé l'essai maison (navigateur) ;
   * - en magasin sans offre transmise (1.5.0, compte déjà servi, offre pas
   *   encore activée dans les consoles) ;
   * - dans un ancien binaire sans achat intégré, où l'on ne vend rien.
   */
  trialOffer(plan?: CheckoutPlan): TrialOffer | null {
    const state = this.subscription();
    if (!this.premiumEnabled() || !state || this.isPremium()) return null;
    if (this.checkout.isOutdatedApp) return null;
    if (this.checkout.isStore) {
      const plans: CheckoutPlan[] = plan ? [plan] : ['YEARLY', 'MONTHLY'];
      const days = Math.max(0, ...plans.map((p) => this.pricing.storeTrialDays(p)));
      return days > 0 ? { days, store: true } : null;
    }
    if (state.trialUsed) return null;
    return { days: TRIAL_DAYS, store: false };
  }

  /** Le récapitulatif de downgrade a été montré. */
  acknowledgeDowngrade(): void {
    this.justDowngraded.set(false);
  }

  /** La célébration a été montrée. */
  acknowledgeUpgrade(): void {
    this.justUpgraded.set(false);
  }

  /** Compte du jeton en main, ou null (pas de jeton, jeton illisible). */
  private currentSub(): string | null {
    if (!this.authApi.getToken()) return null;
    try {
      return this.authApi.getSub() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Un jeton présent et non expiré. Un jeton qui n'est pas un JWT ne se
   * vérifie pas : on tente l'appel, comme le fait `AuthApi.isAuthenticate()`.
   */
  private hasLiveToken(): boolean {
    const token = this.authApi.getToken();
    if (!token) return false;
    if (token.split('.').length !== 3) return true;
    try {
      return !this.authApi.isTokenExpired(token);
    } catch {
      return true;
    }
  }

  /**
   * Met à jour l'état et détecte la transition premium → gratuit.
   *
   * Subtilité : quand la clé locale est ABSENTE (première ouverture, nouvel
   * appareil, cache vidé), on l'initialise sans rien annoncer. Sans cette
   * précaution, tout nouvel utilisateur gratuit verrait un écran « voici ce que
   * tu perds » alors qu'il n'a jamais rien eu — et un abonné de longue date
   * qui change de téléphone serait accueilli par des confettis.
   */
  private applyPremiumState(isPremium: boolean): void {
    const stored = localStorage.getItem(LAST_PREMIUM_KEY);
    const previous = stored === null ? null : stored === 'true';

    // Premium fermé : ni célébration ni « voici ce qui change », il n'y a rien
    // à gagner ni à perdre.
    if (this.premiumEnabled() && previous === true && !isPremium) {
      this.justDowngraded.set(true);
    }
    if (this.premiumEnabled() && previous === false && isPremium) {
      this.justUpgraded.set(true);
    }

    this.isPremium.set(isPremium);
    localStorage.setItem(LAST_PREMIUM_KEY, String(isPremium));
  }
}
